import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import Panel from 'components/Panel';
import Button from 'components/Button';
import SearchMoveForm from 'components/forms/SearchMoveForm';
import SearchResults from 'components/SearchResults';
import SearchResultsList from 'components/SearchResultsList';
import Box from '@mui/material/Box';
import {useDispatch} from 'react-redux';
import {IDiplomatObject} from 'types/DiplomatObject';
import {useGetApi} from 'common/hooks/useGetApi';
import {usePostApi} from 'common/hooks/usePostApi';
import {layoutActions} from 'features/layout/redux';
import {DialogTypes} from 'common/names';
import api from 'api';
import {ISearchCriteria, SearchMethod} from 'components/forms/SearchSchema';

type SearchSynchronizationFormProps = {
   onClose?: () => void;
};

const SearchSynchronizationForm = ({onClose}: SearchSynchronizationFormProps) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();
   const [results, setResults] = React.useState<IDiplomatObject[]>([]);
   const {request: searchNoJobHistorySynchronizations} = useGetApi(
      api.noJobHistorySynchronizations,
      {
         onSuccess: setResults,
      }
   );
   const {request: searchUnusedSynchronizations} = useGetApi(api.unusedSynchronizations, {
      onSuccess: setResults,
   });
   const {request: searchSynchronizations} = useGetApi(api.searchTransactions, {
      onSuccess: setResults,
   });
   const {request: exportSynchronizations} = usePostApi(api.exportSynchronizations, {
      onSuccess: () => {
         dispatch(
            layoutActions.openSubDialog({
               id: DialogTypes.AlertDialog.id,
               title: t('Synchronization Export'),
               content: t('Selected synchronizations successfully exported'),
            })
         );
      },
   });

   const handleSubmit = (values: ISearchCriteria) => {
      if (values.method === SearchMethod.field) {
         searchSynchronizations(
            `?searchType=${values.searchField}&searchText=${values.containingText}`
         );
      }
      if (values.method === SearchMethod.noHistory) {
         searchNoJobHistorySynchronizations();
      }
      if (values.method === SearchMethod.noProcessedFiles) {
         searchUnusedSynchronizations(`?days=${values.daysUnused}`);
      }
      return;
   };

   return (
      <>
         <SearchMoveForm
            searchFieldsLabel={t('Include all synchronizations or find synchronizations with:')}
            searchResult={
               results ? (
                  <Trans t={t}>{{itemsCount: results.length}} synchronizations found.</Trans>
               ) : null
            }
            transaction
            onSubmit={handleSubmit}
         />
         <SearchResults
            label={t('Double-click to edit a synchronization.')}
            data={results}
            resultListComponent={
               <>
                  <Grid item xs={12}>
                     <Panel name={t('Synchronizations')}>
                        <SearchResultsList data={results} />
                     </Panel>
                  </Grid>
               </>
            }
            onExport={exportSynchronizations}
         />
         {onClose && (
            <Box textAlign={'center'}>
               <Button onClick={onClose}>{t('Close')}</Button>
            </Box>
         )}
      </>
   );
};

export default React.memo(SearchSynchronizationForm);
